const PLACEMENT_CLASSES = {
    LOADED: 'promo-placement--loaded',
    STUB: 'promo-placement--stub',
    ERROR: 'promo-placement--error',
    VISIBLE: 'is-visible'
};

const PLACEMENT_A1_ID = 'plc.serp.a1.below_search.v1';
const PLACEMENT_TYPES = {
    BR1: 'br1',
    BR2: 'br2'
};
const ERROR_CODES_TO_HIDE = new Set([404, 402]);

class AdFoxHelper {
    constructor() {
        this.activeContainers = new Set();
        this.adFoxRegisteredContainers = new Set();
        this.containerStates = new Map();
    }

    extractErrorCode(error) {
        if (!error) return null;

        if (error.status !== undefined) return Number(error.status);
        if (error.code !== undefined) return Number(error.code);

        if (error.type !== undefined) {
            const parsed = Number.parseInt(error.type, 10);
            if (!Number.isNaN(parsed)) return parsed;
        }

        const message = error.message;
        if (typeof message === 'string') {
            if (message.includes('404')) return 404;
            if (message.includes('402')) return 402;
        }

        return null;
    }

    getPlacementType(placementId) {
        if (!placementId) return '';
        const parts = placementId.split('.');
        if (parts.length < 3) return '';
        return parts[2].toLowerCase();
    }

    isSidebarPlacement(placementId) {
        const type = this.getPlacementType(placementId);
        return type === PLACEMENT_TYPES.BR1 || type === PLACEMENT_TYPES.BR2;
    }

    isRegistered(containerId) {
        return this.adFoxRegisteredContainers.has(containerId);
    }

    checkAdFoxAvailability() {
        try {
            return !!(window.Ya && window.Ya.adfoxCode);
        } catch (error) {
            console.error('[AdFoxHelper] AdFox API not available:', error);
            return false;
        }
    }

    getContainer(containerId) {
        return document.querySelector(`#${containerId}`);
    }

    resetContainerStyles(container) {
        /* eslint-disable no-param-reassign */
        container.style.visibility = 'visible';
        container.style.position = '';
        container.style.left = '';
        /* eslint-enable no-param-reassign */
    }

    hideContainer(container) {
        /* eslint-disable no-param-reassign */
        container.style.visibility = 'hidden';
        container.style.display = 'block';
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        /* eslint-enable no-param-reassign */
    }

    handlePlacementState(containerId, state, isA1Placement) {
        const container = this.getContainer(containerId);
        if (!container) return;

        this.resetContainerStyles(container);

        const classMap = {
            loaded: [PLACEMENT_CLASSES.LOADED, PLACEMENT_CLASSES.VISIBLE],
            stub: [PLACEMENT_CLASSES.STUB],
            error: [PLACEMENT_CLASSES.ERROR]
        };

        const removeClasses = Object.values(PLACEMENT_CLASSES);
        container.classList.remove(...removeClasses);

        if (classMap[state]) {
            container.classList.add(...classMap[state]);
        }

        if (isA1Placement || state === 'loaded') {
            container.classList.add(PLACEMENT_CLASSES.VISIBLE);
            container.style.display = 'block';
        } else {
            container.style.display = 'none';
        }

        if (state === 'loaded') {
            this.activeContainers.add(containerId);
        }

        this.containerStates.set(containerId, state);
    }

    create({ containerId, customParam1, customParam2, ownerId, placementId, onSidebarError, countryId }) {
        try {
            const container = this.getContainer(containerId);
            if (!container) {
                console.error(`[AdFoxHelper] Container ${containerId} not found`);
                return false;
            }

            const isA1Placement = placementId === PLACEMENT_A1_ID;

            const prevState = this.containerStates.get(containerId);
            if (this.adFoxRegisteredContainers.has(containerId) && (prevState === 'stub' || prevState === 'error')) {
                this.destroyAdFox(containerId);
                this.adFoxRegisteredContainers.delete(containerId);
                this.containerStates.delete(containerId);
                container.innerHTML = '';
                container.classList.remove(...Object.values(PLACEMENT_CLASSES));
            }

            const isReload = this.adFoxRegisteredContainers.has(containerId);
            const isEmpty = !container.innerHTML.trim();

            container.classList.remove(PLACEMENT_CLASSES.STUB, PLACEMENT_CLASSES.ERROR, PLACEMENT_CLASSES.LOADED);

            if (!isReload || isEmpty) {
                this.hideContainer(container);
            }

            const params = {
                p1: customParam1,
                p2: customParam2,
                puid: Date.now()
            };

            if (countryId !== undefined && countryId !== null) {
                params.pcountry = countryId;
            }

            const adfoxParams = {
                ownerId,
                containerId,
                platform: 'desktop',
                params,
                onRender: () => {
                    this.handlePlacementState(containerId, 'loaded', isA1Placement);
                },
                onStub: () => {
                    this.handlePlacementState(containerId, 'stub', isA1Placement);

                    if (this.isSidebarPlacement(placementId) && typeof onSidebarError === 'function') {
                        onSidebarError(placementId);
                    }
                },
                onError: (error) => {
                    this.handlePlacementState(containerId, 'error', isA1Placement);

                    const errorCode = this.extractErrorCode(error);
                    if (ERROR_CODES_TO_HIDE.has(errorCode) && this.isSidebarPlacement(placementId) && typeof onSidebarError === 'function') {
                        onSidebarError(placementId, errorCode);
                    }
                }
            };

            if (this.checkAdFoxAvailability()) {
                try {
                    if (isReload && window.Ya.adfoxCode.reload) {
                        window.Ya.adfoxCode.reload(containerId, params);

                        if (!isEmpty) {
                            this.handlePlacementState(containerId, 'loaded', isA1Placement);
                        }
                    } else {
                        window.Ya.adfoxCode.create(adfoxParams);
                        this.adFoxRegisteredContainers.add(containerId);
                    }
                    return true;
                } catch (error) {
                    console.error(`[AdFoxHelper] Error creating/reloading banner:`, error);
                    this.handlePlacementState(containerId, 'error', isA1Placement);
                    return false;
                }
            }

            window.yaContextCb = window.yaContextCb || [];
            window.yaContextCb.push(() => {
                if (!this.checkAdFoxAvailability()) {
                    console.warn('[AdFoxHelper] AdFox not available');
                    this.handlePlacementState(containerId, 'stub', isA1Placement);
                    return;
                }

                try {
                    window.Ya.adfoxCode.create(adfoxParams);
                    this.adFoxRegisteredContainers.add(containerId);
                } catch (error) {
                    console.error(`[AdFoxHelper] Error creating banner:`, error);
                    this.handlePlacementState(containerId, 'error', isA1Placement);
                }
            });

            return true;
        } catch (error) {
            console.error(`[AdFoxHelper] Error in create:`, error);
            return false;
        }
    }

    destroyAdFox(containerId) {
        try {
            if (this.adFoxRegisteredContainers.has(containerId) && window.Ya?.adfoxCode?.destroy) {
                window.Ya.adfoxCode.destroy(containerId);
            }
        } catch (error) {
            console.warn(`[AdFoxHelper] Error destroying AdFox container:`, error);
        }
    }

    clear(containerId, keepRegistered = false) {
        try {
            const container = this.getContainer(containerId);
            if (container) {
                if (!keepRegistered) {
                    this.destroyAdFox(containerId);
                }
                container.innerHTML = '';
                container.style.display = 'none';
                container.style.visibility = '';
                container.style.position = '';
                container.style.left = '';
                container.classList.remove(...Object.values(PLACEMENT_CLASSES));
                this.activeContainers.delete(containerId);
                if (!keepRegistered) {
                    this.adFoxRegisteredContainers.delete(containerId);
                    this.containerStates.delete(containerId);
                }
            }
        } catch (error) {
            console.error(`[AdFoxHelper] Error clearing:`, error);
        }
    }

    show(containerId) {
        try {
            const container = this.getContainer(containerId);
            if (container) {
                container.style.display = 'block';
                container.classList.add(PLACEMENT_CLASSES.VISIBLE);
            }
        } catch (error) {
            console.error(`[AdFoxHelper] Error showing:`, error);
        }
    }

    hide(containerId) {
        try {
            const container = this.getContainer(containerId);
            if (container) {
                container.style.display = 'none';
                container.classList.remove(PLACEMENT_CLASSES.VISIBLE);
            }
        } catch (error) {
            console.error(`[AdFoxHelper] Error hiding:`, error);
        }
    }
}

let adFoxHelperInstance = null;

export default {
    getInstance() {
        if (!adFoxHelperInstance) {
            adFoxHelperInstance = new AdFoxHelper();
        }
        return adFoxHelperInstance;
    }
};
